import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { 
  FaEnvelope, 
  FaFileAlt, 
  FaPaperclip, 
  FaTimes, 
  FaFileImport, 
  FaEye, 
  FaChevronDown,
  FaTrash,
  FaDownload,
  FaPlus,
  FaRegFileArchive,
  FaRegFilePdf,
  FaRegFileWord,
  FaRegFileExcel,
  FaRegFileImage,
  FaRegFile,
  FaMagic,
  FaSearch,
  FaStar,
  FaClock
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import juice from 'juice';

import { sendFormDataMail } from '../../services/Mail_service';
import SummernoteEditor from '../../components/SummernoteEditor';
import { 
  getAllTemplates, 
  getTemplateById,
  EmailTemplate,
  EmailAttachment,
  getFileUrl
} from '../../services/emailTemplate_service';

// ============================================================================
// CONSTANTES & CONFIGURATION
// ============================================================================

const COLORS = {
  primary: '#4bb0c0',
  primaryGradient: 'linear-gradient(135deg, #4bb0c0 0%, #088395 100%)',
  primaryLight: '#E6F3F5',
  secondary: '#6C757D',
  success: '#28A745',
  danger: '#DC3545',
  warning: '#FFC107',
  info: '#17A2B8',
  light: '#F8F9FA',
  dark: '#343A40',
  white: '#FFFFFF',
  border: '#E9ECEF',
  text: '#212529',
  textMuted: '#6C757D',
  hover: '#F1F3F5'
};

const SHADOWS = {
  sm: '0 2px 4px rgba(0,0,0,0.05)',
  md: '0 4px 6px rgba(0,0,0,0.07)',
  lg: '0 10px 25px rgba(0,0,0,0.1)',
  xl: '0 20px 40px rgba(0,0,0,0.15)'
};

const ANIMATIONS = {
  fadeInUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
    transition: { duration: 0.3 }
  },
  scaleIn: {
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.9 },
    transition: { duration: 0.2 }
  }
};

// ============================================================================
// TYPES
// ============================================================================

interface AttachmentFile {
  name: string;
  size: number;
  url?: string;
  id?: number;
  type?: string;
}

interface TemplateCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;
}

// ============================================================================
// COMPOSANTS UTILITAIRES STYLISÉS
// ============================================================================

const StyledBadge: React.FC<{ 
  label: string; 
  variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'secondary';
  icon?: React.ReactNode;
}> = ({ label, variant = 'secondary', icon }) => {
  const variantStyles = {
    primary: { bg: COLORS.primaryLight, color: COLORS.primary, border: COLORS.primary },
    success: { bg: '#D4EDDA', color: '#155724', border: '#C3E6CB' },
    warning: { bg: '#FFF3CD', color: '#856404', border: '#FFEEBA' },
    danger: { bg: '#F8D7DA', color: '#721C24', border: '#F5C6CB' },
    info: { bg: '#D1ECF1', color: '#0C5460', border: '#BEE5EB' },
    secondary: { bg: '#E2E3E5', color: '#383D41', border: '#D6D8DB' }
  };

  const styles = variantStyles[variant];

  return (
    <span
      className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill"
      style={{
        backgroundColor: styles.bg,
        color: styles.color,
        border: `1px solid ${styles.border}`,
        fontSize: '0.75rem',
        fontWeight: 500
      }}
    >
      {icon && <span style={{ marginRight: '4px' }}>{icon}</span>}
      {label}
    </span>
  );
};

const FileIcon: React.FC<{ fileName: string; size?: number }> = ({ fileName }) => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  
  const iconMap: Record<string, { icon: React.ReactNode; color: string }> = {
    pdf: { icon: <FaRegFilePdf />, color: '#DC3545' },
    doc: { icon: <FaRegFileWord />, color: '#2A579A' },
    docx: { icon: <FaRegFileWord />, color: '#2A579A' },
    xls: { icon: <FaRegFileExcel />, color: '#1F724C' },
    xlsx: { icon: <FaRegFileExcel />, color: '#1F724C' },
    jpg: { icon: <FaRegFileImage />, color: '#6F42C1' },
    jpeg: { icon: <FaRegFileImage />, color: '#6F42C1' },
    png: { icon: <FaRegFileImage />, color: '#6F42C1' },
    gif: { icon: <FaRegFileImage />, color: '#6F42C1' },
    zip: { icon: <FaRegFileArchive />, color: '#FD7E14' },
    rar: { icon: <FaRegFileArchive />, color: '#FD7E14' }
  };

  const defaultIcon = { icon: <FaRegFile />, color: COLORS.secondary };
  const { icon, color } = extension ? iconMap[extension] || defaultIcon : defaultIcon;

  return (
    <span style={{ color, fontSize: '1.2rem', display: 'flex', alignItems: 'center' }}>
      {icon}
    </span>
  );
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// ============================================================================
// COMPOSANT D'APERÇU DES TEMPLATES (AMÉLIORÉ)
// ============================================================================

interface TemplatePreviewModalProps {
  show: boolean;
  onClose: () => void;
  template: EmailTemplate | null;
}

const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({ 
  show, 
  onClose, 
  template 
}) => {
  if (!template) return null;

  return (
    <Modal 
      show={show} 
      onHide={onClose} 
      size="lg" 
      centered
      dialogClassName="template-preview-modal"
    >
      <Modal.Header 
        closeButton 
        style={{ 
          background: COLORS.primaryGradient,
          color: COLORS.white,
          border: 'none',
          padding: '1.5rem'
        }}
      >
        <Modal.Title className="d-flex align-items-center gap-3">
          <div
            style={{
              width: '40px',
              height: '40px',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FaFileAlt size={20} />
          </div>
          <div>
            <h5 style={{ margin: 0, fontWeight: 600 }}>{template.name}</h5>
            <small style={{ opacity: 0.9 }}>Aperçu du template</small>
          </div>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ padding: 0 }}>
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Métadonnées */}
          <div style={{ 
            padding: '1.5rem',
            background: COLORS.light,
            borderBottom: `1px solid ${COLORS.border}`
          }}>
            <div className="d-flex align-items-center gap-3">
              <StyledBadge 
                label={`Créé le ${new Date(template.created_at || '').toLocaleDateString('fr-FR')}`} 
                variant="info"
                icon={<FaClock size={10} />}
              />
              {template.attachments && template.attachments.length > 0 && (
                <StyledBadge 
                  label={`${template.attachments.length} pièce(s) jointe(s)`}
                  variant="success"
                  icon={<FaPaperclip size={10} />}
                />
              )}
            </div>
          </div>

          {/* Sujet */}
          <div style={{ padding: '1.5rem', borderBottom: `1px solid ${COLORS.border}` }}>
            <div style={{ 
              fontSize: '0.85rem', 
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: COLORS.textMuted,
              marginBottom: '0.5rem',
              fontWeight: 600
            }}>
              Objet
            </div>
            <div style={{ 
              padding: '1rem',
              background: COLORS.white,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              fontWeight: 500
            }}>
              {template.subject}
            </div>
          </div>
          
          {/* Contenu */}
          <div style={{ padding: '1.5rem', borderBottom: `1px solid ${COLORS.border}` }}>
            <div style={{ 
              fontSize: '0.85rem', 
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: COLORS.textMuted,
              marginBottom: '0.5rem',
              fontWeight: 600
            }}>
              Contenu
            </div>
            <div 
              style={{ 
                padding: '1.5rem',
                background: COLORS.white,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                maxHeight: '400px',
                overflowY: 'auto'
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: template.content }} />
            </div>
          </div>

          {/* Pièces jointes */}
        {template.attachments && template.attachments.length > 0 && (
          <div style={{ 
            position: 'sticky',
            bottom: 0,
            background: COLORS.white,
            borderTop: `1px solid ${COLORS.border}`,
            padding: '1.5rem',
            zIndex: 10,
            boxShadow: '0 -4px 10px rgba(0,0,0,0.05)'
          }}>
            <div style={{ 
              fontSize: '0.85rem', 
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: COLORS.textMuted,
              marginBottom: '1rem',
              fontWeight: 600
            }}>
              Pièces jointes
            </div>
            <div className="row g-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {template.attachments.map((attachment: EmailAttachment) => (
                <div key={attachment.id} className="col-md-6">
                  <div
                    style={{
                      padding: '1rem',
                      background: COLORS.white,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem'
                    }}
                  >
                    <FileIcon fileName={attachment.file_name} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {attachment.file_name}
                      </div>
                      <small style={{ color: COLORS.textMuted }}>
                        {formatFileSize(attachment.file_size)}
                      </small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        </div>
      </Modal.Body>
      
      <Modal.Footer style={{ borderTop: `1px solid ${COLORS.border}`, padding: '1rem 1.5rem' }}>
        <Button 
          variant="outline-secondary" 
          onClick={onClose}
          style={{
            padding: '0.5rem 1.5rem',
            borderRadius: '6px',
            fontWeight: 500
          }}
        >
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ============================================================================
// NOUVEAU : CARTE DE TEMPLATE MODERNE
// ============================================================================

interface TemplateCardProps {
  template: EmailTemplate;
  onApply: () => void;
  onPreview: () => void;
  isSelected: boolean;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ 
  template, 
  onApply, 
  onPreview, 
  isSelected 
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      style={{
        background: COLORS.white,
        border: `2px solid ${isSelected ? COLORS.primary : COLORS.border}`,
        borderRadius: '12px',
        padding: '1.25rem',
        cursor: 'pointer',
        position: 'relative',
        boxShadow: isHovered ? SHADOWS.md : SHADOWS.sm,
        transition: 'all 0.2s ease'
      }}
      onClick={onApply}
    >
      {/* Badge de sélection */}
      {isSelected && (
        <div
          style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            width: '24px',
            height: '24px',
            background: COLORS.primaryGradient,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: COLORS.white,
            fontSize: '12px',
            boxShadow: SHADOWS.sm
          }}
        >
          ✓
        </div>
      )}

      {/* En-tête de la carte */}
      <div className="d-flex align-items-start justify-content-between mb-3">
        <div
          style={{
            width: '40px',
            height: '40px',
            background: COLORS.primaryLight,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: COLORS.primary
          }}
        >
          <FaFileAlt size={18} />
        </div>
        
        <Button
          variant="link"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onPreview();
          }}
          style={{
            color: COLORS.secondary,
            padding: '4px 8px',
            background: isHovered ? COLORS.hover : 'transparent',
            borderRadius: '6px',
            transition: 'all 0.2s ease'
          }}
          title="Aperçu"
        >
          <FaEye size={14} />
        </Button>
      </div>

      {/* Contenu */}
      <div style={{ marginBottom: '1rem' }}>
        <h6 style={{ 
          fontWeight: 600, 
          marginBottom: '0.25rem',
          color: COLORS.text
        }}>
          {template.name}
        </h6>
        <p style={{ 
          fontSize: '0.85rem', 
          color: COLORS.textMuted,
          margin: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}>
          {template.subject}
        </p>
      </div>

      {/* Métadonnées */}
      <div className="d-flex align-items-center gap-2">
        {template.attachments && template.attachments.length > 0 && (
          <StyledBadge 
            label={template.attachments.length.toString()}
            variant="info"
            icon={<FaPaperclip size={10} />}
          />
        )}
      </div>

      {/* Bouton d'application (apparaît au survol) */}
      <AnimatePresence>
        {isHovered && !isSelected && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            style={{
              position: 'absolute',
              bottom: '1rem',
              right: '1rem'
            }}
          >
            <Button
              size="sm"
              style={{
                background: COLORS.primaryGradient,
                border: 'none',
                padding: '0.25rem 1rem',
                borderRadius: '20px',
                fontSize: '0.8rem',
                fontWeight: 500,
                boxShadow: SHADOWS.sm
              }}
              onClick={(e) => {
                e.stopPropagation();
                onApply();
              }}
            >
              Appliquer
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ============================================================================
// NOUVEAU : GRILLE DE TEMPLATES MODERNE
// ============================================================================

interface TemplateGridProps {
  templates: EmailTemplate[];
  selectedTemplate: EmailTemplate | null;
  loading: boolean;
  onApplyTemplate: (template: EmailTemplate) => void;
  onPreview: (template: EmailTemplate) => void;
}

const TemplateGrid: React.FC<TemplateGridProps> = ({
  templates,
  selectedTemplate,
  loading,
  onApplyTemplate,
  onPreview
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);


  // Filtrer les templates
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.subject.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Barre de recherche */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <FaSearch 
            size={14} 
            style={{
              position: 'absolute',
              left: '12px',
              color: COLORS.textMuted
            }}
          />
          <input
            type="text"
            placeholder="Rechercher un template..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem 0.75rem 2.5rem',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '10px',
              fontSize: '0.95rem',
              outline: 'none',
              transition: 'all 0.2s ease'
            }}
            onFocus={(e) => e.target.style.borderColor = COLORS.primary}
            onBlur={(e) => e.target.style.borderColor = COLORS.border}
          />
        </div>
      </div>

      {/* État de chargement */}
      {loading && (
        <div className="text-center py-5">
          <Spinner 
            animation="border" 
            style={{ color: COLORS.primary, width: '3rem', height: '3rem' }} 
          />
          <p style={{ color: COLORS.textMuted, marginTop: '1rem' }}>
            Chargement des templates...
          </p>
        </div>
      )}

      {/* Grille de templates */}
      {!loading && (
        <>
          {filteredTemplates.length === 0 ? (
            <div 
              className="text-center py-5"
              style={{
                background: COLORS.light,
                borderRadius: '12px',
                padding: '3rem'
              }}
            >
              <FaFileAlt size={48} style={{ color: COLORS.textMuted, marginBottom: '1rem' }} />
              <h6 style={{ color: COLORS.text, fontWeight: 600 }}>Aucun template trouvé</h6>
              <p style={{ color: COLORS.textMuted, fontSize: '0.9rem' }}>
                Aucun template ne correspond à votre recherche.
              </p>
            </div>
          ) : (
            <div className="row g-3">
              {filteredTemplates.map(template => (
                <div key={template.id} className="col-md-6">
                  <TemplateCard
                    template={template}
                    onApply={() => onApplyTemplate(template)}
                    onPreview={() => onPreview(template)}
                    isSelected={selectedTemplate?.id === template.id}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

// ============================================================================
// NOUVEAU : ZONE DE DÉPÔT DE FICHIERS
// ============================================================================

interface DropZoneProps {
  onFileDrop: (files: FileList) => void;
  children: React.ReactNode;
}

const DropZone: React.FC<DropZoneProps> = ({ onFileDrop, children }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      onFileDrop(e.dataTransfer.files);
    }
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        border: `2px dashed ${isDragging ? COLORS.primary : COLORS.border}`,
        borderRadius: '12px',
        padding: '1.5rem',
        background: isDragging ? COLORS.primaryLight : 'transparent',
        transition: 'all 0.2s ease',
        cursor: 'pointer'
      }}
    >
      {children}
    </div>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL AMÉLIORÉ
// ============================================================================

interface EmailModalProps {
  show: boolean;
  onClose: () => void;
  recipientName: string;
  recipientEmail: string;
  currentUserEmail: string;
  fromUserListPoint?: boolean;
  email?: string;
  subject?: string;
  message?: string;
  oldPoints?: number;
  newPoints?: number;
  minAnnonceAmount?: boolean;
}

const EmailModal: React.FC<EmailModalProps> = ({
  show,
  onClose,
  recipientName,
  recipientEmail,
  currentUserEmail,
  fromUserListPoint,
  oldPoints,
  newPoints,
  minAnnonceAmount
}) => {
  const navigate = useNavigate();
  
  // États
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  
  const [formData, setFormData] = useState({
    subject: '',
    content: '',
    attachments: [] as File[],
    templateAttachments: [] as AttachmentFile[]
  });

  // Refs
  const htmlInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // ==========================================================================
  // CHARGEMENT DES TEMPLATES
  // ==========================================================================

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await getAllTemplates(1, 100);
      setTemplates(response.data);
    } catch (error) {
      console.error('❌ Erreur chargement templates:', error);
      toast.error('Impossible de charger les templates');
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // APPLICATION D'UN TEMPLATE
  // ==========================================================================

  const applyTemplate = async (template: EmailTemplate) => {
    try {
      setLoading(true);
      
      const response = await getTemplateById(template.id);
      const fullTemplate = response.data;
      
      // Variables de remplacement
      const nameParts = recipientName.split(' ');
      const variables = {
        'prenom': nameParts[0] || recipientName,
        'nom': nameParts.slice(1).join(' ') || '',
        'email': recipientEmail,
        'date': new Date().toLocaleDateString('fr-FR'),
        'company': recipientName,
        'telephone': '',
        'adresse': ''
      };
      
      // Remplacer les variables
      const processedSubject = replaceVariables(fullTemplate.subject, variables);
      const processedContent = replaceVariables(fullTemplate.content, variables);
      
      setSelectedTemplate(fullTemplate);
      setFormData(prev => ({
        ...prev,
        subject: processedSubject,
        content: processedContent,
        templateAttachments: fullTemplate.attachments?.map(att => ({
          id: att.id,
          name: att.file_name,
          url: getFileUrl(att.file_path),
          size: att.file_size,
          type: att.file_type
        })) || []
      }));
      
      setShowTemplateSelector(false);
      toast.success(
        <div>
          <strong>✅ Template appliqué</strong>
          <br />
          <small>{fullTemplate.name}</small>
        </div>
      );
      
    } catch (error: any) {
      toast.error(error.message || '❌ Erreur lors de l\'application du template');
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // UTILITAIRES
  // ==========================================================================

  const replaceVariables = (text: string, variables: Record<string, string>): string => {
    let result = text;
    Object.entries(variables).forEach(([key, value]) => {
      result = result.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    return result;
  };

  const inlineStyles = (htmlContent: string): string => {
    if (!htmlContent) return '';
    try {
      return juice(htmlContent, {
        inlinePseudoElements: true,
        preserveImportant: true,
        removeStyleTags: true,
        applyWidthAttributes: true,
        applyHeightAttributes: true,
        applyAttributesTableElements: true
      });
    } catch (error) {
      console.error('❌ Erreur inlining styles:', error);
      return htmlContent;
    }
  };

  const resetForm = () => {
    setFormData({
      subject: '',
      content: '',
      attachments: [],
      templateAttachments: []
    });
    setSelectedTemplate(null);
  };

  // ==========================================================================
  // GESTIONNAIRES D'ÉVÉNEMENTS
  // ==========================================================================

  useEffect(() => {
    if (show) {
      loadTemplates();
      resetForm();
    }
  }, [show]);

  useEffect(() => {
    if (!show) return;

    if (minAnnonceAmount) {
      setFormData({
        subject: "Demande : annonce inférieure à 0.5 €",
        content: generateMinAnnonceContent(),
        attachments: [],
        templateAttachments: []
      });
    } else if (fromUserListPoint) {
      setFormData({
        subject: "Mise à jour de votre solde de points",
        content: generatePointsUpdateContent(recipientName, oldPoints, newPoints),
        attachments: [],
        templateAttachments: []
      });
    }
  }, [show, fromUserListPoint, minAnnonceAmount, recipientName, oldPoints, newPoints]);

  const generateMinAnnonceContent = (): string => `
    <div style="font-family: Arial, sans-serif; font-size: 15px; color: #333; line-height: 1.6;">
      <p>Bonjour l'équipe ALO,</p>
      <p>Je souhaite vous informer que l'annonce que je souhaite publier a un montant inférieur à 0.5 € et ne peut pas être traitée automatiquement par la plateforme.</p>
      <p>Pourriez-vous, s'il vous plaît, vérifier et m'aider à procéder à la publication ou à ajuster le montant si nécessaire ?</p>
      <p>Merci pour votre aide.</p>
      <p style="margin-top: 20px;">Cordialement,</p>
    </div>
  `;

  const generatePointsUpdateContent = (name: string, oldPoints?: number, newPoints?: number): string => `
    <div style="font-family: Arial, sans-serif; font-size: 15px; color: #333; line-height: 1.6;">
      <p>Bonjour <strong>${name}</strong>,</p>
      <p>Nous vous informons que votre solde de points a été mis à jour dans notre système.</p>
      <table style="border-collapse: collapse; margin: 15px 0; width: 100%; max-width: 400px;">
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Ancien solde</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${oldPoints || 0} points</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Nouveau solde</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${newPoints || 0} points</td>
        </tr>
      </table>
      <p>Si vous avez des questions, n'hésitez pas à nous contacter.</p>
      <p style="margin-top: 25px;">Cordialement,<br/>L'équipe ALO</p>
    </div>
  `;

  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        const content = event.target.result as string;
        setFormData(prev => ({ 
          ...prev, 
          content: inlineStyles(content) 
        }));
        toast.success('✅ Fichier HTML importé');
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleAttachmentAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...Array.from(e.target.files!)]
      }));
      toast.success(`${e.target.files.length} fichier(s) ajouté(s)`);
    }
  };

  const handleFileDrop = (files: FileList) => {
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...Array.from(files)]
    }));
    toast.success(`${files.length} fichier(s) ajouté(s) par glisser-déposer`);
  };

  const handleAttachmentRemove = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!recipientEmail) {
      toast.warning('⚠️ Adresse email du destinataire manquante');
      return;
    }
    if (!formData.subject) {
      toast.warning('⚠️ Veuillez saisir un objet');
      return;
    }

    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('sender', currentUserEmail);
      formDataToSend.append('object', formData.subject);
      formDataToSend.append('message', inlineStyles(formData.content));
      formDataToSend.append('bcc', JSON.stringify([recipientEmail]));

      // Pièces jointes utilisateur
      formData.attachments.forEach(file => {
        formDataToSend.append('attachments', file);
      });

      // Pièces jointes du template
      if (formData.templateAttachments.length > 0) {
        const toastId = toast.loading('📎 Téléchargement des pièces jointes...');
        
        for (const attachment of formData.templateAttachments) {
          if (attachment.url) {
            try {
              const response = await fetch(attachment.url);
              const blob = await response.blob();
              const file = new File([blob], attachment.name, { type: blob.type });
              formDataToSend.append('attachments', file);
            } catch (error) {
              console.error(`❌ Erreur téléchargement ${attachment.name}:`, error);
              toast.error(`Impossible de joindre ${attachment.name}`);
            }
          }
        }
        
        toast.dismiss(toastId);
      }

      await sendFormDataMail(formDataToSend);
      
      toast.success('✅ Email envoyé avec succès');
      handleClose();
      
      if (minAnnonceAmount) {
        navigate('/home/gestion-annonces');
      }
    } catch (err: any) {
      console.error('❌ Erreur envoi:', err);
      toast.error(err.message || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // ==========================================================================
  // RENDU PRINCIPAL
  // ==========================================================================

  return (
    <>
      <Modal 
        show={show} 
        onHide={handleClose} 
        centered 
        size={showTemplateSelector ? "xl" : "lg"}
        backdrop="static"
        dialogClassName="email-modal"
        ref={modalRef}
      >
        <Modal.Header 
          closeButton 
          style={{ 
            background: COLORS.primaryGradient,
            color: COLORS.white,
            border: 'none',
            padding: '1.5rem'
          }}
        >
          <Modal.Title className="d-flex align-items-center gap-3">
            <div
              style={{
                width: '45px',
                height: '45px',
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FaEnvelope size={20} />
            </div>
            <div>
              <h5 style={{ margin: 0, fontWeight: 600 , color:'white'}}>
                {minAnnonceAmount ? 'Demande spéciale' : 'Nouveau message'}
              </h5>
              <small style={{ opacity: 0.9 , color:'white'}}>
                Pour : {recipientName} • {recipientEmail}
              </small>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: 0 }}>
          {/* Barre d'outils */}
          {!fromUserListPoint && !minAnnonceAmount && (
            <div
              style={{
                padding: '1rem 1.5rem',
                background: COLORS.light,
                borderBottom: `1px solid ${COLORS.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Button
                variant={showTemplateSelector ? 'primary' : 'outline-primary'}
                onClick={() => setShowTemplateSelector(!showTemplateSelector)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  fontWeight: 500,
                  background: showTemplateSelector ? COLORS.primaryGradient : 'transparent',
                  border: `1px solid ${showTemplateSelector ? 'transparent' : COLORS.primary}`,
                  color: showTemplateSelector ? COLORS.white : COLORS.primary
                }}
              >
                <FaMagic size={14} />
                {showTemplateSelector ? 'Masquer les templates' : 'Utiliser un template'}
              </Button>

              {selectedTemplate && (
                <StyledBadge 
                  label={`Template: ${selectedTemplate.name}`}
                  variant="success"
                  icon={<FaFileAlt size={10} />}
                />
              )}
            </div>
          )}

          {/* Sélecteur de templates */}
          <AnimatePresence>
            {showTemplateSelector && (
              <motion.div
                {...ANIMATIONS.fadeInUp}
                style={{
                  padding: '1.5rem',
                  borderBottom: `1px solid ${COLORS.border}`,
                  maxHeight: '400px',
                  overflowY: 'auto'
                }}
              >
                <TemplateGrid
                  templates={templates}
                  selectedTemplate={selectedTemplate}
                  loading={loading}
                  onApplyTemplate={applyTemplate}
                  onPreview={(template) => {
                    setPreviewTemplate(template);
                    setShowPreviewModal(true);
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>
{/* Formulaire */}
<div style={{ padding: '1.5rem' }}>
  {/* Alertes contextuelles */}
  {minAnnonceAmount && (
    <Alert 
      variant="warning" 
      className="mb-4"
      style={{
        borderRadius: '10px',
        border: 'none',
        background: '#FFF3CD',
        color: '#856404'
      }}
    >
      <div className="d-flex align-items-center gap-2">
        <span>⚠️</span>
        <span>
          Cette annonce ne peut pas être créée automatiquement (montant &lt; 0.5€). 
          Veuillez envoyer votre demande par email.
        </span>
      </div>
    </Alert>
  )}

  <Form onSubmit={handleSubmit}>
    {/* Zone défilante pour le contenu du formulaire */}
    <div style={{ maxHeight: '500px', overflowY: 'auto', paddingRight: '5px', marginBottom: '1.5rem' }}>
      {/* Objet */}
      <Form.Group className="mb-4">
        <Form.Label className="fw-semibold" style={{ color: COLORS.text }}>
          Objet
        </Form.Label>
        <Form.Control
          type="text"
          value={formData.subject}
          onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
          required
          placeholder="Objet de l'email"
          style={{
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            border: `1px solid ${COLORS.border}`,
            fontSize: '0.95rem'
          }}
        />
      </Form.Group>

      {/* Contenu */}
      <Form.Group className="mb-4">
        <Form.Label className="fw-semibold" style={{ color: COLORS.text }}>
          Message
        </Form.Label>
        <SummernoteEditor
          value={formData.content}
          onChange={(content) => setFormData(prev => ({ ...prev, content }))}
        />
      </Form.Group>

      {/* Pièces jointes du template */}
      {formData.templateAttachments.length > 0 && (
        <div className="mb-4">
          <Form.Label className="fw-semibold" style={{ color: COLORS.text }}>
            Pièces jointes du template
          </Form.Label>
          <div
            style={{
              background: COLORS.light,
              borderRadius: '10px',
              padding: '1rem'
            }}
          >
            {formData.templateAttachments.map((attachment, index) => (
              <div
                key={attachment.id || index}
                className="d-flex align-items-center gap-3"
                style={{
                  padding: '0.75rem',
                  background: COLORS.white,
                  borderRadius: '8px',
                  marginBottom: index < formData.templateAttachments.length - 1 ? '0.5rem' : 0
                }}
              >
                <FileIcon fileName={attachment.name} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {attachment.name}
                  </div>
                  <small style={{ color: COLORS.textMuted }}>
                    {formatFileSize(attachment.size)}
                  </small>
                </div>
                {attachment.url && (
                  <Button
                    variant="link"
                    size="sm"
                    href={attachment.url}
                    target="_blank"
                    style={{ color: COLORS.info }}
                    title="Télécharger"
                  >
                    <FaDownload />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gestionnaire de pièces jointes */}
      {!fromUserListPoint && !minAnnonceAmount && (
        <div className="mb-4">
          <Form.Label className="fw-semibold" style={{ color: COLORS.text }}>
            Pièces jointes
          </Form.Label>
          
          <DropZone onFileDrop={handleFileDrop}>
            <div className="text-center">
              <FaPaperclip 
                size={24} 
                style={{ color: COLORS.textMuted, marginBottom: '0.5rem' }} 
              />
              <p style={{ color: COLORS.textMuted, marginBottom: '0.5rem' }}>
                Glissez-déposez vos fichiers ici ou
              </p>
              <div className="d-flex gap-2 justify-content-center">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => htmlInputRef.current?.click()}
                  style={{
                    borderRadius: '6px',
                    padding: '0.4rem 1rem',
                    fontSize: '0.9rem'
                  }}
                >
                  Importer HTML
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    borderRadius: '6px',
                    padding: '0.4rem 1rem',
                    fontSize: '0.9rem'
                  }}
                >
                  <FaPlus className="me-1" size={12} />
                  Ajouter des fichiers
                </Button>
              </div>
            </div>
          </DropZone>

          <input
            type="file"
            accept=".html"
            ref={htmlInputRef}
            onChange={handleFileImport}
            style={{ display: 'none' }}
          />

          <input
            type="file"
            multiple
            ref={fileInputRef}
            onChange={handleAttachmentAdd}
            style={{ display: 'none' }}
          />

          {/* Liste des pièces jointes */}
          {formData.attachments.length > 0 && (
            <div className="mt-3">
              {formData.attachments.map((file, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="d-flex align-items-center gap-3"
                  style={{
                    padding: '0.75rem',
                    background: COLORS.white,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    marginBottom: '0.5rem'
                  }}
                >
                  <FileIcon fileName={file.name} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {file.name}
                    </div>
                    <small style={{ color: COLORS.textMuted }}>
                      {formatFileSize(file.size)}
                    </small>
                  </div>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => handleAttachmentRemove(index)}
                    style={{ color: COLORS.danger }}
                    title="Supprimer"
                  >
                    <FaTrash />
                  </Button>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>

{/* Boutons d'action - POSITION FIXE */}
<div 
  className="d-flex justify-content-end gap-2" 
  style={{ 
    position: 'sticky',
    bottom: 0,
    background: COLORS.white,
    borderTop: `1px solid ${COLORS.border}`,
    padding: '1.5rem',
    marginTop: 'auto',
    zIndex: 10,
    boxShadow: '0 -4px 10px rgba(0,0,0,0.05)'
  }}
>
  <Button
    variant="outline-secondary"
    onClick={handleClose}
    style={{
      padding: '0.6rem 1.5rem',
      borderRadius: '8px',
      fontWeight: 500
    }}
  >
    Annuler
  </Button>
  <Button
    variant="primary"
    type="submit"
    disabled={submitting}
    style={{
      background: COLORS.primaryGradient,
      border: 'none',
      padding: '0.6rem 2rem',
      borderRadius: '8px',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    }}
  >
    {submitting ? (
      <>
        <Spinner size="sm" />
        Envoi en cours...
      </>
    ) : (
      <>
        <FaEnvelope />
        Envoyer
      </>
    )}
  </Button>
</div>
  </Form>
</div>
        </Modal.Body>
      </Modal>

      {/* Modal d'aperçu */}
      <TemplatePreviewModal
        show={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewTemplate(null);
        }}
        template={previewTemplate}
      />

      {/* Styles additionnels */}
      <style>
        {`
          .email-modal .modal-content {
            border: none;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: ${SHADOWS.xl};
          }
          
          .email-modal .modal-header .btn-close {
            filter: brightness(0) invert(1);
            opacity: 0.8;
          }
          
          .email-modal .modal-header .btn-close:hover {
            opacity: 1;
          }
          
          .template-preview-modal .modal-content {
            border: none;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: ${SHADOWS.xl};
          }
        `}
      </style>
    </>
  );
};

export default EmailModal;